import { z } from 'zod'

// expiresAt is on the browser's clock, derived from Pilote's remaining time rather than from its
// absolute expiry, so a workstation whose clock drifts from Pilote's still schedules its checks right.
export type PiloteSessionStatus =
  | { readonly status: 'valid'; readonly expiresAt: number; readonly maxInactiveMillis: number }
  | { readonly status: 'expired' }

const PiloteSessionResponseZod = z.object({
  expiresInMillis: z.number(),
  maxInactiveIntervalSeconds: z.number(),
})

const callPiloteSession = async (
  path: string,
  method: 'GET' | 'POST',
  session: string
): Promise<PiloteSessionStatus> => {
  const sentAt = Date.now()
  const response = await fetch(`${import.meta.env.VITE_PILOTE_BASE_URL}${path}`, {
    method,
    credentials: 'omit',
    cache: 'no-store',
    headers: { 'X-Pilote-Session': session },
  })
  if (response.status === 401) {
    return { status: 'expired' }
  }
  if (!response.ok) {
    throw new Error(`Pilote ${method} ${path} answered ${response.status}`)
  }
  const { expiresInMillis, maxInactiveIntervalSeconds } = PiloteSessionResponseZod.parse(await response.json())
  return { status: 'valid', expiresAt: sentAt + expiresInMillis, maxInactiveMillis: maxInactiveIntervalSeconds * 1000 }
}

export const peekPiloteSession = (session: string) => callPiloteSession('/auth/session', 'GET', session)

export const touchPiloteSession = (session: string) => callPiloteSession('/auth/session/touch', 'POST', session)
